import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon, TruckIcon } from '@hero/web-icons';
import { usePublishOrderStore, type PublishOrderLocationDraft } from '@hero/hooks-shared';
import { Card } from '../../../../app/Card/Card';
import { Counter } from '../../../../app/Counter/Counter';
import { Button } from '../../../../ui/Button/Button';
import { OrderPriceEditDrawer } from '../../Drawer/OrderPriceEditDrawer';
import { DestinationPriceChip } from './DestinationPriceChip';
import { LocationPickerDrawer } from './LocationPickerDrawer';
import { LocationPickerRow } from './LocationPickerRow';
import { SectionHeader } from './SectionHeader';

/**
 * Route step: one shared `LocationPickerDrawer` instance is opened from
 * the origin row or any destination row. The active slot is held in
 * local state — the drawer's `onPick` writes to the right Zustand
 * setter and closes itself.
 */
export function RouteSection() {
  const { t } = useTranslation(['m-order', 'c-main']);

  const origin = usePublishOrderStore(s => s.origin);
  const destinations = usePublishOrderStore(s => s.destinations);
  const customer = usePublishOrderStore(s => s.customer);
  const setOrigin = usePublishOrderStore(s => s.setOrigin);
  const setDestination = usePublishOrderStore(s => s.setDestination);
  const addDestination = usePublishOrderStore(s => s.addDestination);
  const removeDestination = usePublishOrderStore(s => s.removeDestination);

  type PickerSlot = { kind: 'origin' } | { kind: 'destination'; index: number };
  const [pickerSlot, setPickerSlot] = useState<PickerSlot | null>(null);
  // Per-destination edit-price drawer — opened from the chip on a
  // destination footer. Holds only the index; the drawer pulls the
  // current override + resolver-provided price out of the publish
  // store on render.
  const [priceEditIndex, setPriceEditIndex] = useState<number | null>(null);

  const handlePick = (loc: PublishOrderLocationDraft) => {
    if (!pickerSlot) return;
    if (pickerSlot.kind === 'origin') setOrigin(loc);
    else setDestination(pickerSlot.index, loc);
    setPickerSlot(null);
  };

  const headerTitle =
    pickerSlot?.kind === 'origin'
      ? t('wizard.location.origin_title', { defaultValue: 'Pick origin' })
      : pickerSlot
      ? t('wizard.location.destination_title', {
          defaultValue: 'Pick destination {{n}}',
          n: pickerSlot.index + 1,
        })
      : undefined;

  // Tell the drawer which slot is being edited so it can skip its own
  // current pick when computing the "already picked elsewhere" set —
  // otherwise editing destination 2 would treat destination 2's
  // existing location as a forbidden duplicate.
  const excludeLocation: PublishOrderLocationDraft | null =
    pickerSlot?.kind === 'origin'
      ? origin
      : pickerSlot?.kind === 'destination'
      ? destinations[pickerSlot.index] ?? null
      : null;

  // When the slot the picker was opened for already holds a location,
  // open the drawer directly into edit (refine) mode instead of the
  // search list. That way clicking "Edit" or the body of a filled
  // destination card lands the user on the same form they'd see for a
  // brand-new save — no extra hop through the search step. Saved
  // locations also pipe their `savedLocationId` through so the
  // adapter loads + locks the record (place type / linked client
  // can't be changed through this surface).
  //
  // Memoized: the drawer's `useEffect([isOpen, initialEdit])` and
  // downstream `useSavedLocationCreateAdapter` effects key off this
  // object's identity. A fresh literal each render would re-trigger
  // them every parent render and ultimately loop the map's
  // `setLocations` effect (max-update-depth crash).
  const slotInitialEdit = useMemo<
    { savedLocationId?: string; draft?: PublishOrderLocationDraft } | undefined
  >(() => {
    if (!excludeLocation || excludeLocation.lat === 0) return undefined;
    if (excludeLocation.savedLocationId) {
      return { savedLocationId: excludeLocation.savedLocationId };
    }
    return { draft: excludeLocation };
  }, [excludeLocation]);

  // A destination row gets a clear/remove button when:
  //   - it has a location (clearing wipes the slot), or
  //   - there are 2+ destination rows (so an empty extra can be
  //     removed without forcing the user to first pick a location).
  // The single-destination case keeps the row sticky: there is always
  // at least one destination slot rendered.
  const canRemoveAnyDestination = destinations.length > 1;

  return (
    <Card className="p-4">
      <SectionHeader
        title={t('wizard.route.title', { defaultValue: 'Route' })}
        subtitle={t('wizard.route.subtitle', {
          defaultValue: 'Pick a pickup point and one or more drop-offs.',
        })}
      />

      <div className="mt-3 space-y-2">
        <LocationPickerRow
          tone="origin"
          label={t('form.origin.title', { defaultValue: 'Origin' })}
          location={origin}
          onOpen={() => setPickerSlot({ kind: 'origin' })}
          onClear={origin ? () => setOrigin(null) : undefined}
        />

        {destinations.map((dest, idx) => (
          <DestinationRow
            key={idx}
            index={idx}
            destination={dest}
            canRemoveEmpty={canRemoveAnyDestination}
            onOpen={() => setPickerSlot({ kind: 'destination', index: idx })}
            onRemove={() => removeDestination(idx)}
            onEditPrice={() => setPriceEditIndex(idx)}
          />
        ))}

        <Button
          variant="outline"
          onClick={addDestination}
          className="w-full justify-center gap-1.5">
          <PlusIcon className="size-4" />
          {t('wizard.destinations.add', { defaultValue: 'Add destination' })}
        </Button>
      </div>

      <LocationPickerDrawer
        isOpen={pickerSlot !== null}
        onClose={() => setPickerSlot(null)}
        title={headerTitle}
        clientId={customer?.customerId}
        excludeLocation={excludeLocation}
        initialEdit={slotInitialEdit}
        onPick={handlePick}
      />

      <OrderPriceEditDrawer
        mode="publish-draft"
        isOpen={priceEditIndex !== null}
        destinationIndex={priceEditIndex ?? 0}
        onClose={() => setPriceEditIndex(null)}
      />
    </Card>
  );
}

interface DestinationRowProps {
  index: number;
  destination: PublishOrderLocationDraft;
  /**
   * `true` when the row is allowed to be removed even before it has
   * a location picked — used when the user has added an empty extra
   * destination and wants to take it back off without filling it
   * first. The single-destination invariant is maintained at the
   * parent ({@link removeDestination} keeps at least one slot).
   */
  canRemoveEmpty: boolean;
  onOpen: () => void;
  onRemove: () => void;
  onEditPrice: () => void;
}

function DestinationRow({
  index,
  destination,
  canRemoveEmpty,
  onOpen,
  onRemove,
  onEditPrice,
}: DestinationRowProps) {
  const { t } = useTranslation('m-order');
  const quantity = usePublishOrderStore(s => s.destinationVehicleQuantities[index] ?? 1);
  const setQuantity = usePublishOrderStore(s => s.setDestinationVehicleQuantity);
  const hasLocation = destination.lat !== 0;

  return (
    <LocationPickerRow
      tone="dest"
      label={t('form.destination.title', {
        defaultValue: 'Destination {{n}}',
        n: index + 1,
        // older translations still use {{index}} — keep both substitutions valid.
        index: index + 1,
      })}
      location={hasLocation ? destination : null}
      onOpen={onOpen}
      onClear={hasLocation || canRemoveEmpty ? onRemove : undefined}
      footer={
        hasLocation ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500">
              <TruckIcon className="size-4 text-gray-500" />
              <span>{t('wizard.vehicle_count', { defaultValue: 'Trucks' })}</span>
            </div>
            <div className="flex items-center gap-3">
              <DestinationPriceChip index={index} onEdit={onEditPrice} />
              <Counter value={quantity} onChange={n => setQuantity(index, n)} min={1} max={10} />
            </div>
          </div>
        ) : undefined
      }
    />
  );
}
